"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/hooks/useAuth";
import { apiFetch } from "@/lib/api/client";
import { presentRewardLabel, splitRewardLabel, type GrantedItem, type PresentReward } from "@/lib/presents";
import type { ClaimedPresent } from "@/lib/presents-server";
import type { LoginPending, PresentsResponse } from "@/app/api/presents/route";
import { REWARDS_POLL_EVENT } from "@/components/ui/SkillUnlockToast";
import WaxSeal from "@/components/ui/WaxSeal";
import { DateStamp, DoneSeal, InkFilter, PencilRing, ReceivedStamp } from "./LoginBonusCard";
import { useUiTexts } from "@/lib/ui-texts-client";
import css from "./LoginBonusWindow.module.css";

/** 窓に出す行の数（今日の前後。カードの端では寄せる） */
const VISIBLE_ROWS = 5;

/** 「あとで」を押した日（uid と累計の日）。この端末だけに覚える。使えない端末では開いているあいだだけ覚える */
const SEEN_KEY = "library:login-window";
let seenFallback: string | null = null;
function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY) ?? seenFallback;
  } catch {
    return seenFallback;
  }
}
function writeSeen(value: string) {
  seenFallback = value;
  try {
    localStorage.setItem(SEEN_KEY, value);
  } catch {
    // 覚えられない端末では、開き直すとまた出る
  }
}

/** 届くもの1つ（絵があれば絵・名前・数だけ太く） */
function Reward({ label, imageUrl }: { label: string; imageUrl?: string }) {
  const { name, amount } = splitRewardLabel(label);
  return (
    <span className={css.reward}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      {imageUrl && <img src={imageUrl} alt="" />}
      {name}
      {amount && <b>{amount}</b>}
    </span>
  );
}

const rewardImage = (r: PresentReward) => (r.type === "material" || r.type === "spirit" ? r.imageUrl : undefined);

/**
 * その日はじめてホームを開いたときの、貸出カードの窓（10/22 本人が見本の案1＋案2を選んだ）。
 * 今日のログインボーナス（login.pending の今日の分）をまだ受け取っていなければ出す。
 * 今日の行に朱の日付の判が押され、「受け取る」でその場で受け取る（プレゼントボックスと同じ受け取りの処理）。
 * 「あとで」は窓を閉じるだけで、ログインボーナスの画面の届くものを押して受け取れる（その日はもう窓を出さない。受け取れる数はホームのしおりに出る・10/23）
 */
export default function LoginBonusWindow() {
  const { user, userData, reloadUserData } = useAuth();
  const { t } = useUiTexts();
  const [state, setState] = useState<{ login: PresentsResponse["login"]; pending: LoginPending } | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [granted, setGranted] = useState<GrantedItem[] | null>(null);
  const [error, setError] = useState(false);

  const uid = user?.uid;
  const loginDays = userData?.loginDays ?? 0;
  const loginBonusCount = userData?.loginBonusCount ?? 0;

  useEffect(() => {
    // 受け取っていない贈り物が無い日・もう窓を出した日は、問い合わせもしない
    if (!user || loginDays < 1 || loginBonusCount < 1 || readSeen() === `${user.uid}:${loginDays}`) return;
    let cancelled = false;
    (async () => {
      const token = await user.getIdToken();
      const r = await apiFetch<PresentsResponse>("/api/presents", token).catch(() => null);
      if (cancelled || !r) return;
      const pending = r.login.pending.find((p) => p.day === r.login.days);
      if (pending) setState({ login: r.login, pending });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, loginDays, loginBonusCount]);

  if (!state || typeof document === "undefined") return null;
  const { login, pending } = state;
  const today = login.days;
  // 今日届くもの（絵つき）。カードの今日の行から取り、行が無ければ受け取り待ちの名前だけ
  const todayRow = login.card.find((row) => row.day === today);
  const todayItems = todayRow
    ? [
        ...todayRow.rewards.map((r) => ({ label: presentRewardLabel(r), imageUrl: rewardImage(r) })),
        ...todayRow.goalItems.map((item) => ({ label: item.label, imageUrl: item.imageUrl || undefined })),
      ]
    : pending.labels.map((label) => ({ label, imageUrl: undefined }));

  // 今日の前後の行（カードの1行目・10行目の近くでは、同じカードの中で5行になるよう寄せる）
  const first = login.card[0]?.day ?? today;
  const last = login.card[login.card.length - 1]?.day ?? today;
  const start = Math.max(first, Math.min(today - 2, last - (VISIBLE_ROWS - 1)));
  const rows = login.card.filter((row) => row.day >= start && row.day < start + VISIBLE_ROWS);

  async function claim() {
    if (!user || claiming) return;
    setClaiming(true);
    setError(false);
    try {
      const token = await user.getIdToken();
      const r = await apiFetch<{ claimed: ClaimedPresent[] }>("/api/presents/claim", token, { method: "POST", body: { ids: pending.ids } });
      setGranted(r.claimed.flatMap((p) => p.granted));
      writeSeen(`${uid}:${today}`);
      // ログインボーナスのしおりの数を取り直す
      await reloadUserData();
    } catch {
      setError(true);
    } finally {
      setClaiming(false);
    }
  }

  function close() {
    writeSeen(`${uid}:${today}`);
    setState(null);
    // 受け取ったときの「報酬獲得！」の知らせは、窓を閉じてから上に出す
    if (granted) window.dispatchEvent(new Event(REWARDS_POLL_EVENT));
  }

  return createPortal(
    <div className={css.backdrop}>
      <div role="dialog" aria-modal="true" aria-label={t("quests.loginWindow.aria")} className={css.window}>
        <p className={css.eyebrow}>{t("quests.loginWindow.eyebrow")}</p>
        <div className={css.card}>
          <InkFilter />
          <span className={css.hole} aria-hidden="true" />
          <div className={css.head}>
            <span>{t("quests.loginWindow.cardTitle")}</span>
            <b>{today}<small>{t("quests.loginWindow.dayUnit")}</small></b>
          </div>
          <div role="table">
            <div role="row" className={css.cols}>
              <span role="columnheader">{t("quests.presents.loginCard.colDay")}</span>
              <span role="columnheader">{t("quests.presents.loginCard.colDate")}</span>
              <span role="columnheader">{t("quests.presents.loginCard.colRewards")}</span>
            </div>
            {rows.map((row) => {
              const isToday = row.day === today;
              return (
                <div key={row.day} role="row" className={css.row} data-today={isToday || undefined} data-later={row.day > today || undefined}>
                  <span role="cell" className={css.day}>
                    {row.day}
                    {isToday && <PencilRing />}
                  </span>
                  <span role="cell">
                    {row.stamped &&
                      (row.date ? (
                        <span className={isToday ? css.press : undefined}><DateStamp day={row.day} text={row.date} framed={isToday} /></span>
                      ) : (
                        <DoneSeal day={row.day} text={t("quests.presents.loginCard.noDate")} />
                      ))}
                  </span>
                  {/* 受け取った日（窓で今いま受け取った今日も）は「受領」の判を右端に押す */}
                  <span role="cell" className={css.what} data-received={row.received || (isToday && granted) || undefined}>
                    {/* ログイン報酬と、報酬一覧の「ログイン日数」のノードの報酬（10/22）を並べる */}
                    {row.rewards.length + row.goalItems.length > 0
                      ? [
                          ...row.rewards.map((r, i) => <Reward key={`r${i}`} label={presentRewardLabel(r)} imageUrl={rewardImage(r)} />),
                          ...row.goalItems.map((item, i) => <Reward key={`g${i}`} label={item.label} imageUrl={item.imageUrl || undefined} />),
                        ]
                      : <span className={css.none}>{t("quests.presents.loginCard.none")}</span>}
                    {(row.received || (isToday && granted)) && <ReceivedStamp day={row.day} text={t("quests.presents.loginCard.received")} press={isToday && !!granted} />}
                  </span>
                </div>
              );
            })}
          </div>
          <div className={css.got}>
            <WaxSeal size={46} rotate={-8} />
            <div>
              <small>{granted ? t("quests.loginWindow.got") : t("quests.loginWindow.today")}</small>
              <ul className={css.gotList}>
                {granted
                  ? granted.map((item, i) => <li key={i}><Reward label={item.label} imageUrl={item.imageUrl || undefined} /></li>)
                  : todayItems.map((item, i) => <li key={i}><Reward label={item.label} imageUrl={item.imageUrl} /></li>)}
              </ul>
            </div>
          </div>
        </div>
        {error && <p className={css.error}>{t("quests.presents.error")}</p>}
        {granted ? (
          <button type="button" className={css.leather} onClick={close}>{t("quests.loginWindow.close")}</button>
        ) : (
          <>
            <button type="button" className={css.leather} onClick={claim} disabled={claiming}>
              {claiming ? t("quests.loginWindow.claiming") : t("quests.loginWindow.claim")}
            </button>
            <button type="button" className={css.later} onClick={close} disabled={claiming}>{t("quests.loginWindow.later")}</button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
